import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatBalance,
  formatDate,
  formatDateTime,
  formatMoney,
  parseMoneyInput,
  t,
  tr,
  type MessageKey,
} from '@aquabox/shared';
import { statusText } from '../lib/labels';
import { ApiError } from '../api/client';
import {
  useCustomer,
  useCustomerDeposits,
  useCustomerMutations,
  useCustomerOrderSummary,
  useCustomerSummary,
  useCustomerTxns,
  useCustomers,
  useFinanceMutations,
  useOrders,
  useRegions,
} from '../api/queries';
import { ChannelPill, StatusDot } from '../components/Pills';
import { BalanceCell, balanceCell } from '../components/BalanceCell';
import { Icon } from '../components/Icon';
import { Kpi } from '../components/Kpi';
import { useToast } from '../components/Toast';
import { useColumnWidths } from '../components/useColumnWidths';
import { relativeTime } from '../lib/labels';
import type { Customer, Order } from '../api/types';

const FILTERS: { value: string; labelKey: Parameters<typeof t>[0] }[] = [
  { value: 'all', labelKey: 'panel.filterAll' },
  { value: 'debtors', labelKey: 'panel.filterDebtors' },
  { value: 'withDeposit', labelKey: 'panel.filterWithDeposit' },
  { value: 'lapsed', labelKey: 'panel.filterLapsed' },
];

/** Müşteriler ekranı (§3.8): KPI, filtre chip'leri, tablo, sağda müşteri kartı. */
export function Customers() {
  const [params] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Ön Muhasebe → Cari hesaplar "Tahsilat al" (§3.13.2) buraya derin bağlantıyla düşer:
  // ilgili müşteri kartı VE tahsilat formu açık gelmeli.
  const [selectedId, setSelectedId] = useState<string | null>(() => params.get('customer'));
  const autoOpenCollect = params.get('collect') === '1';
  // Deep-link: ?customer=<id> syncs selection when URL changes while on the page
  useEffect(() => {
    const id = params.get('customer');
    if (id) setSelectedId(id);
  }, [params]);
  // Yeni / düzenle formunun modu: null kapalı, 'create' yeni müşteri, 'edit' mevcut müşteriyi
  // düzenle. Düzenle modunda `customer` doludur ve form o satırla açılır.
  const [form, setForm] = useState<{ mode: 'create' | 'edit'; customer: Customer | null } | null>(
    null,
  );
  // "Pasifleri göster" anahtarı — `includeInactive=true` ile listeyi genişletir. Pasif müşteri
  // için kart sağda "Yeniden etkinleştir" düğmesini gösterir; listede pasif rozeti vardır.
  const [includeInactive, setIncludeInactive] = useState(false);
  const { style: colStyle, headerRef, resizer } = useColumnWidths('customers', 6);

  // KPI toplamları TÜM aktif müşteriler üzerinden ayrı bir uçtan gelir; sayfalanmış listenin
  // `pageSize` tavanına (backend'de en fazla 200) bağlı DEĞİLDİR — 200'den fazla müşteride bile
  // doğru sayı gösterir (Faz 1 denetimi U5: önceden `pageSize=200`'ü "tümü" sayıyordu).
  const summary = useCustomerSummary();
  const list = useCustomers({
    filter: filter === 'all' ? undefined : filter,
    q: search || undefined,
    pageSize: '200',
    includeInactive: includeInactive ? 'true' : undefined,
  });
  const detail = useCustomer(selectedId);

  const totals = {
    count: summary.data?.totalCustomers ?? 0,
    receivable: Number(summary.data?.totalReceivable ?? '0'),
    jugs: summary.data?.totalJugsOnHand ?? 0,
  };

  const items = list.data?.items ?? [];

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      <div className="page" style={{ flex: 1, minWidth: 0 }}>
        <section className="kpi-grid" style={{ '--kpi-cols': 3 } as React.CSSProperties}>
          <Kpi
            label={t('panel.totalReceivable')}
            value={formatMoney(totals.receivable)}
            accent={totals.receivable > 0 ? 'warning' : undefined}
          />
          <Kpi label={t('panel.registeredCustomers')} value={totals.count} />
          <Kpi label={t('panel.jugsAtCustomers')} value={totals.jugs} />
        </section>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              style={{
                borderRadius: 'var(--r-pill)',
                padding: '10px 15px',
                background: filter === f.value ? 'var(--primary)' : 'var(--surface)',
                border: `1px solid ${filter === f.value ? 'var(--primary)' : 'var(--border)'}`,
                color: filter === f.value ? '#fff' : 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {t(f.labelKey)}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {/* Pasifleri göster: includeInactive=true ile listeyi genişletir. Sunucu bunu
              zod'un `z.coerce.boolean` ile kabul eder; "false" göndermek listeyi etkisizce
              aktiflere kısar. Panel toggle kapalıyken anahtarı HİÇ göndermez. */}
          <label
            data-testid="customers-include-inactive"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-secondary)',
              userSelect: 'none',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {t('cust.includeInactive')}
          </label>
          <input
            className="input"
            style={{ width: '100%', maxWidth: 260, minWidth: 0, height: 38 }}
            placeholder={t('panel.searchCustomers')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('panel.searchCustomers')}
          />
          <button
            type="button"
            data-testid="customers-new-btn"
            className="btn btn-primary"
            onClick={() => setForm({ mode: 'create', customer: null })}
            style={{ height: 38 }}
          >
            {t('cust.add')}
          </button>
        </div>

        <div className="card" style={{ overflow: 'auto' }}>
          <div
            ref={headerRef}
            className="table-head customers-grid"
            style={{ padding: '0 22px', height: 44, ...colStyle }}
          >
            <div className="th-cell">
              <span>{t('panel.colCustomer')}</span>
              {resizer(0)}
            </div>
            <div className="th-cell">
              <span>{t('panel.phone')}</span>
              {resizer(1)}
            </div>
            <div className="th-cell col-hide-sm">
              <span>{t('panel.colRegion')}</span>
              {resizer(2)}
            </div>
            <div className="th-cell">
              <span style={{ textAlign: 'right' }}>{t('panel.balance')}</span>
              {resizer(3)}
            </div>
            <div className="th-cell col-hide-sm">
              <span style={{ textAlign: 'right' }}>{t('panel.colJugsAtCustomer')}</span>
              {resizer(4)}
            </div>
            <div className="th-cell">
              <span>{t('panel.lastOrder')}</span>
              {resizer(5)}
            </div>
          </div>

          {list.isPending ? (
            <p
              style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              {t('common.loading')}
            </p>
          ) : list.isError ? (
            <div
              style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <p style={{ marginBottom: 12 }}>{t('panel.loadFailed')}</p>
              <button type="button" className="btn" onClick={() => void list.refetch()}>
                {t('panel.retry')}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div
              style={{ padding: '52px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <Icon name="funnel" size={56} />
              <p style={{ marginTop: 14, fontWeight: 500 }}>{t('panel.noCustomersFound')}</p>
              {(filter !== 'all' || search !== '' || includeInactive) && (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: 14 }}
                  onClick={() => {
                    setFilter('all');
                    setSearch('');
                    setIncludeInactive(false);
                  }}
                >
                  {t('cust.clearFilter')}
                </button>
              )}
            </div>
          ) : (
            <ul className="rowgrid-stack-list" style={{ listStyle: 'none', minWidth: 900 }}>
              {items.map((customer) => (
                <li key={customer.id}>
                  <button
                    onClick={() => setSelectedId(customer.id)}
                    className="customers-grid"
                    style={{
                      width: '100%',
                      padding: '0 22px',
                      height: 58,
                      borderTop: '1px solid var(--border)',
                      background: selectedId === customer.id ? 'var(--primary-bg)' : 'transparent',
                      textAlign: 'left',
                      font: 'inherit',
                      opacity: customer.isActive ? 1 : 0.6,
                      ...colStyle,
                    }}
                  >
                    <span style={{ fontWeight: 500, fontSize: 14 }}>
                      {customer.fullName}
                      {!customer.isActive && (
                        <span
                          data-testid={`customer-inactive-badge-${customer.id}`}
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            padding: '1px 5px',
                          }}
                        >
                          {t('cust.inactive')}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
                      {customer.phone ?? '—'}
                    </span>
                    <span className="col-hide-sm" style={{ fontSize: 13.5 }}>
                      {customer.region?.name ?? '—'}
                    </span>
                    <BalanceCell balance={customer.balance} fontSize={13.5} />
                    <span
                      className="col-hide-sm"
                      style={{ textAlign: 'right', fontWeight: 600, fontSize: 13.5 }}
                    >
                      {customer.jugsOnHand}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {customer.lastOrderAt ? relativeTime(customer.lastOrderAt) : t('panel.never')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selectedId && detail.data && (
        <CustomerCard
          customer={detail.data}
          onClose={() => setSelectedId(null)}
          autoOpenCollect={autoOpenCollect}
          onEdit={() => setForm({ mode: 'edit', customer: detail.data })}
        />
      )}
      {form && (
        <CustomerForm mode={form.mode} customer={form.customer} onClose={() => setForm(null)} />
      )}
    </div>
  );
}

/**
 * Bakiye hücresi tek yerden gelir: `components/BalanceCell.tsx` — müşteri ekstresiyle
 * aynı dil ("₺136,50 B" / "₺25,00 A"), renk yönden türetilir (para hesabı yapılmaz).
 */

/** Müşteri kartı paneli (§3.8 D). Bakiye ve zimmet sunucuda defterden hesaplanır. */
function CustomerCard({
  customer,
  onClose,
  autoOpenCollect,
  onEdit,
}: {
  customer: Customer;
  onClose: () => void;
  autoOpenCollect?: boolean;
  onEdit: () => void;
}) {
  const { text, color, debtor } = balanceCell(customer.balance);
  const { deactivate, reactivate } = useCustomerMutations();
  const toast = useToast();
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);

  return (
    <aside
      style={{
        width: 'var(--drawer-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span className="section-label">{t('panel.customerCard')}</span>
        <span style={{ flex: 1 }} />
        {/* K3: anonimleştirilmiş kayıt geri açılamaz; sunucu tüm PATCH'leri 409 ile reddeder.
            Düzenle ve Yeniden Etkinleştir düğmelerini gizliyoruz — göstermek tıklanınca 409
            almak demek, kullanıcıyı yorar. Deaktif/Pasifleştir akışı da anonim kayıt için
            kapalı: bakiye/zimmet zaten sıfır olmalı yoksa zaten şifreli kayıt olmaz. */}
        {customer.anonymizedAt ? null : (
          <button
            type="button"
            data-testid="customers-edit-btn"
            onClick={onEdit}
            className="btn"
            style={{ fontSize: 12.5, padding: '6px 12px' }}
          >
            {t('cust.edit')}
          </button>
        )}
        {customer.anonymizedAt ? null : customer.isActive ? (
          <button
            type="button"
            data-testid="customers-deactivate-btn"
            onClick={() => setConfirmDeactivate(true)}
            className="btn"
            style={{
              fontSize: 12.5,
              padding: '6px 12px',
              color: 'var(--danger)',
            }}
          >
            {t('cust.deactivate')}
          </button>
        ) : (
          <button
            type="button"
            data-testid="customers-reactivate-btn"
            onClick={() => setConfirmReactivate(true)}
            className="btn"
            style={{ fontSize: 12.5, padding: '6px 12px', color: 'var(--primary)' }}
          >
            {t('cust.reactivate')}
          </button>
        )}
        <button
          onClick={onClose}
          aria-label={t('common.cancel')}
          style={{ color: 'var(--text-secondary)' }}
        >
          <Icon name="x" />
        </button>
      </header>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 17 }}>
            {customer.fullName}
            {!customer.isActive && (
              <span
                style={{
                  marginLeft: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '2px 6px',
                }}
              >
                {t('cust.inactive')}
              </span>
            )}
          </div>
          {customer.phone && (
            <a href={`tel:${customer.phone}`} style={{ fontSize: 14.5, fontWeight: 500 }}>
              {customer.phone}
            </a>
          )}
          <div style={{ fontSize: 14, lineHeight: 1.5, marginTop: 4 }}>
            {customer.address}
            {customer.region && (
              <span style={{ color: 'var(--text-secondary)' }}> · {customer.region.name}</span>
            )}
          </div>
          {customer.addressNote && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {customer.addressNote}
            </div>
          )}
        </div>

        <CustomerOrdersBox customerId={customer.id} />

        <div
          style={{
            borderRadius: 'var(--r-box)',
            padding: 18,
            background: debtor ? 'var(--bg-danger-box)' : 'var(--page-bg)',
            border: `1px solid ${debtor ? 'var(--danger)' : 'var(--border)'}`,
          }}
        >
          <div className="section-label">{t('panel.balance')}</div>
          <div
            className="kpi-number-sm"
            style={{ marginTop: 8, color }}
            data-testid="customer-card-balance"
          >
            {text}
          </div>
          {/* B/A ekinin anlamı — bir kez, kart üzerinde (ekstreyle aynı dil). */}
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--text-secondary)',
              marginTop: 6,
              lineHeight: 1.4,
            }}
            title={t('statement.balanceLegend')}
          >
            {t('statement.balanceLegend')}
          </div>
          <CollectForm
            customerId={customer.id}
            balance={customer.balance}
            autoOpen={autoOpenCollect}
          />
        </div>

        <div style={{ background: 'var(--page-bg)', borderRadius: 'var(--r-box)', padding: 16 }}>
          <div className="section-label">{t('panel.deposit')}</div>
          <div style={{ fontSize: 14.5, fontWeight: 500, marginTop: 6 }}>
            {customer.jugsOnHand > 0
              ? t('panel.jugsWithCustomer', { n: customer.jugsOnHand })
              : t('panel.noJugsWithCustomer')}
          </div>
          <DepositBreakdown customerId={customer.id} />
        </div>

        <OrderHabitBox customerId={customer.id} />

        <OrderHistory customerId={customer.id} />

        <LedgerEntries customerId={customer.id} />
      </div>

      {confirmDeactivate && (
        <div
          data-testid="customers-deactivate-confirm"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: 'var(--r-box)',
              padding: 24,
              maxWidth: 420,
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
              {t('cust.deactivateConfirm', { name: customer.fullName })}
            </p>
            {deactivate.isError && (
              <p
                data-testid="customers-deactivate-error"
                style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}
              >
                {(deactivate.error as { message?: string } | null)?.message ?? t('error.internal')}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn"
                style={{ flex: 1 }}
                onClick={() => setConfirmDeactivate(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-testid="customers-deactivate-confirm-btn"
                className="btn btn-primary"
                style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}
                disabled={deactivate.isPending}
                onClick={() =>
                  deactivate.mutate(customer.id, {
                    onSuccess: () => {
                      toast(t('cust.deactivated', { name: customer.fullName }), 'success');
                      setConfirmDeactivate(false);
                      onClose();
                    },
                  })
                }
              >
                {t('cust.deactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmReactivate && (
        <div
          data-testid="customers-reactivate-confirm"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: 'var(--r-box)',
              padding: 24,
              maxWidth: 420,
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            {/* U3: onay kutusunda kullanıcının neyi onayladığını GÖRMESİ için soru metni.
                İsim tek başına bağlamsız ("bu ne?"). Sunucu 409 ile reddederse mesajı
                buraya düşer (sunucu Türkçe döner, panel genel "işlem başarısız"a
                çevirmez). */}
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
              {t('cust.reactivateConfirm', { name: customer.fullName })}
            </p>
            {reactivate.isError && (
              <p
                data-testid="customers-reactivate-error"
                style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}
              >
                {(reactivate.error as { message?: string } | null)?.message ?? t('error.internal')}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn"
                style={{ flex: 1 }}
                onClick={() => setConfirmReactivate(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-testid="customers-reactivate-confirm-btn"
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={reactivate.isPending}
                onClick={() =>
                  reactivate.mutate(customer.id, {
                    onSuccess: () => {
                      toast(t('cust.reactivated', { name: customer.fullName }), 'success');
                      setConfirmReactivate(false);
                    },
                  })
                }
              >
                {t('cust.reactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * Yeni müşteri / düzenle formu.
 *
 * Modal katmanı olarak değil, sağdan açılan drawer içinde gösterilir — Customers ekranının
 * kendi drawer yarısının ÜSTÜNE biner. Alan adları `customers.schema.ts` ile birebir.
 *
 * Update şeması üç niyet ayırt eder (müşteri formu da öyle):
 *   - alan başta dolu, kullanıcı BOŞALTIRSA → `null` (sunucu DB'ye NULL yazar).
 *   - alan başta boşsa, kullanıcı DOKUNMAZSA → gövdede YOK (sunucu mevcudu korur).
 *   - alan başta dolu, kullanıcı DEĞİŞTİRİRSE → yeni string.
 * Bu yüzden create'te olduğu gibi "boşsa hiç yazma" kestirmesi burada ÇALIŞMAZ; eski değer
 * olduğu gibi kalırdı. Diff: `initial.X !== trimmed` → fark varsa yaz, `initial.X` doluysa
 * boşaltıldıysa `null` yaz.
 *
 * Bölge listesi için `useRegions` kullanılır. `regionId` "Adresten otomatik" seçildiyse
 * (`''`) → `null` gider ve sunucu adresten yeniden eşleştirir; aksi hâlde seçilen id yazılır.
 *
 * Sunucu hatası 400/409'da formun içinde Türkçe gösterilir; 400'de alan-bazlı mesaj varsa o
 * gösterilir, yoksa sunucunun `message` alanı.
 */
function CustomerForm({
  mode,
  customer,
  onClose,
}: {
  mode: 'create' | 'edit';
  customer: Customer | null;
  onClose: () => void;
}) {
  const regions = useRegions();
  const { create, update } = useCustomerMutations();
  const toast = useToast();
  // Düzenle modunda her alanın BAŞLANGIÇ değeri `initial` ile kilitlenir. "Boşaldı mı" kararı
  // `initial.X !== trimmed` ile değil, ESKİSİ BOŞ OLMADIĞI HALDE YENİSİ BOŞ OLMUŞSA ile
  // verilir (PATCH'te `null` göndereceğiz, aksi hâlde alanı tamamen düşürürüz).
  const initial = useMemo(
    () => ({
      fullName: customer?.fullName ?? '',
      phone: customer?.phone ?? '',
      // phone2 / notes başta boş geliyordu → form her açılışta sıfırdan doluyordu.
      // Müşteri telefon2'si DB'de varsa artık dolu geliyor.
      phone2: customer?.phone2 ?? '',
      address: customer?.address ?? '',
      addressNote: customer?.addressNote ?? '',
      regionId: customer?.region?.id ?? '',
      notes: customer?.notes ?? '',
    }),
    [customer],
  );
  const [fullName, setFullName] = useState(initial.fullName);
  const [phone, setPhone] = useState(initial.phone);
  const [phone2, setPhone2] = useState(initial.phone2);
  const [address, setAddress] = useState(initial.address);
  const [addressNote, setAddressNote] = useState(initial.addressNote);
  const [regionId, setRegionId] = useState(initial.regionId);
  const [notes, setNotes] = useState(initial.notes);
  const [error, setError] = useState<string | null>(null);

  function submit(): void {
    setError(null);
    if (mode === 'create') {
      if (!fullName.trim() || !phone.trim() || !address.trim()) {
        setError(t('error.validation'));
        return;
      }
      const body: Record<string, unknown> = {
        fullName: fullName.trim(),
        phone: phone.trim(),
        address: address.trim(),
      };
      // Boş isteğe bağlı alanlar gönderilMEZ — sunucu şeması `min(1)` istediği için boş string
      // kabul etmez (Customers ekranı görev kabul kriteri).
      if (phone2.trim()) body.phone2 = phone2.trim();
      if (addressNote.trim()) body.addressNote = addressNote.trim();
      if (regionId) body.regionId = regionId;
      if (notes.trim()) body.notes = notes.trim();
      create.mutate(body, {
        onSuccess: (saved: Customer) => {
          toast(t('cust.created', { name: saved.fullName }), 'success');
          onClose();
        },
        onError: (err: unknown) => setError(formError(err)),
      });
      return;
    }

    // Düzenle: her alan için üç niyet ayrılır:
    //   - başta yok, kullanıcı yazdı    → string
    //   - başta vardı, kullanıcı boşalttı → null (sunucu DB'ye NULL yazar)
    //   - başta vardı, kullanıcı değiştirdi → yeni string
    //   - başta yoktu, kullanıcı dokunmadı → alan gövdede YOK (sunucu mevcudu korur)
    // Aşağıdaki `diff` karşılaştırması bu dörtlüyü kodlar; YAPAY 'dokunma' eşleşmesi yok.
    const body: Record<string, unknown> = {};
    const trimmedFullName = fullName.trim();
    if (trimmedFullName !== initial.fullName) body.fullName = trimmedFullName;
    const trimmedPhone = phone.trim();
    if (trimmedPhone !== initial.phone) body.phone = trimmedPhone;
    const trimmedPhone2 = phone2.trim();
    if (initial.phone2 !== '' && trimmedPhone2 === '') body.phone2 = null;
    else if (trimmedPhone2 !== initial.phone2) body.phone2 = trimmedPhone2;
    const trimmedAddress = address.trim();
    if (trimmedAddress !== initial.address) body.address = trimmedAddress;
    // U1: adres değiştiyse VE bölge "adresten otomatik"teyse sunucuya `regionId: null`
    // gönder; yoksa eski bölge yeni adrese yapışır (sunucu `regionId` yokken mevcudu
    // korur, bu yüzden bilinçli `null` şart). Bölge açıkça seçildiyse onu yaz; kullanıcı
    // bölgeye dokunmadıysa (initial'a eşit) ve adres de değişmediyse gövdede OLMAZ.
    if (trimmedAddress !== initial.address && regionId === '') {
      body.regionId = null;
    }
    const trimmedAddressNote = addressNote.trim();
    if (initial.addressNote !== '' && trimmedAddressNote === '') body.addressNote = null;
    else if (trimmedAddressNote !== initial.addressNote) body.addressNote = trimmedAddressNote;
    if (regionId !== initial.regionId) body.regionId = regionId || null;
    const trimmedNotes = notes.trim();
    if (initial.notes !== '' && trimmedNotes === '') body.notes = null;
    else if (trimmedNotes !== initial.notes) body.notes = trimmedNotes;

    if (Object.keys(body).length === 0) {
      // Hiçbir şey değişmemiş — kullanıcıya yazma yok.
      onClose();
      return;
    }

    update.mutate(
      { id: customer!.id, ...body },
      {
        onSuccess: (saved: Customer) => {
          toast(t('cust.updated', { name: saved.fullName }), 'success');
          onClose();
        },
        onError: (err: unknown) => setError(formError(err)),
      },
    );
  }

  return (
    <aside
      data-testid="customers-form"
      style={{
        width: 'var(--drawer-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span className="section-label">
          {mode === 'create' ? t('cust.formTitleNew') : t('cust.formTitleEdit')}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          aria-label={t('common.cancel')}
          onClick={onClose}
          style={{ color: 'var(--text-secondary)' }}
        >
          <Icon name="x" />
        </button>
      </header>
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('cust.fullName')}
          </span>
          <input
            data-testid="customer-form-fullName"
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('cust.phone')}</span>
          <input
            data-testid="customer-form-phone"
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('cust.phone2')}</span>
          <input
            data-testid="customer-form-phone2"
            className="input"
            value={phone2}
            onChange={(e) => setPhone2(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('cust.address')}
          </span>
          <input
            data-testid="customer-form-address"
            className="input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('cust.addressNote')}
          </span>
          <input
            data-testid="customer-form-addressNote"
            className="input"
            value={addressNote}
            onChange={(e) => setAddressNote(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('cust.region')}</span>
          <select
            data-testid="customer-form-regionId"
            className="input"
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
          >
            <option value="">{t('cust.regionAuto')}</option>
            {(regions.data?.items ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('cust.notes')}</span>
          <textarea
            data-testid="customer-form-notes"
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </label>
        {error && (
          <p
            data-testid="customer-form-error"
            style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}
          >
            {error}
          </p>
        )}
      </div>
      <footer
        style={{
          padding: '16px 24px',
          display: 'flex',
          gap: 8,
          borderTop: '1px solid var(--border)',
        }}
      >
        <button type="button" className="btn" style={{ flex: 1 }} onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          data-testid="customer-form-save"
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={create.isPending || update.isPending}
          onClick={submit}
        >
          {t('common.save')}
        </button>
      </footer>
    </aside>
  );
}

/**
 * Form hata metni: 400/409 ApiError'ı kendi mesajıyla, başka her şey genel metne düşer.
 * Mevcut desenle (Regions.tsx) tutarlı — sunucu Türkçe döner, olduğu gibi gösterilir.
 */
function formError(err: unknown): string {
  if (err instanceof ApiError) return err.message || t('cust.saveFailed');
  return t('cust.saveFailed');
}

/**
 * "Siparişler" özet kutusu (kullanıcı kararı 29.09.2026). Müşteri kartının EN ÜSTÜNDE — patron
 * müşteriyi açar açmaz toplam sipariş sayısını ve bunların kaçının bayi panelinde (Hayat/Abant)
 * kapandığını görür.
 *
 * `useCustomerOrderSummary` zaten `orderCount` (tüm zamanlar) ve `externalCount` (HANDLED_EXTERNALLY
 * yani panel) dönüyor; yeni uç yok. Yükleniyorken skeleton göstermek yerine kartı gizli tutuyoruz
 * — kısa bir gecikme patronun gözünden kaçar, sıfır görmek "hiç sipariş yok" izlenimi verir.
 */
function CustomerOrdersBox({ customerId }: { customerId: string }) {
  const summary = useCustomerOrderSummary(customerId);
  if (summary.isPending || summary.isError || !summary.data) return null;
  if (summary.data.orderCount === 0) return null;
  // "Panelde N" yalnız externalCount > 0 iken eklenir: 0'da "Panelde 0" demek
  // gürültü, panelin asıl amacı Hayat'ta kapanan siparişlerin görünürlüğü.
  const parts: string[] = [t('panelRep.customerOrdersSummary', { total: summary.data.orderCount })];
  if (summary.data.externalCount > 0) {
    parts.push(t('panelRep.customerOrdersPanel', { panel: summary.data.externalCount }));
  }
  return (
    <div
      data-testid="customer-orders-box"
      style={{
        borderRadius: 'var(--r-box)',
        padding: 18,
        background: 'var(--page-bg)',
        border: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 14 }}>{t('panelRep.customerOrdersTitle')}</span>
      <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{parts.join(' · ')}</span>
    </div>
  );
}

/**
 * "Sipariş alışkanlığı" kutusu (§3.8 D.6).
 *
 * `GET /api/customers/:id/order-summary` ucundan beslenir. Üst satır parçaları (ortalama
 * aralık / tipik gün / son sipariş) yalnız değeri varsa gösterilir ve " · " ile ayrılır.
 * Panel para hesabı yapmaz (kural 4): `completedTotal` sunucudan string gelir, `formatMoney`
 * ile biçimlenir.
 */
function OrderHabitBox({ customerId }: { customerId: string }) {
  const summary = useCustomerOrderSummary(customerId);
  const data = summary.data;
  return (
    <div style={{ background: 'var(--primary-bg)', borderRadius: 'var(--r-box)', padding: 16 }}>
      <div className="section-label" data-testid="custHist-title">
        {t('custHist.title')}
      </div>
      {summary.isPending ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 6 }}>
          {t('custHist.loading')}
        </p>
      ) : summary.isError ? (
        <p style={{ fontSize: 13.5, color: 'var(--danger)', marginTop: 6 }}>
          {t('custHist.error')}
        </p>
      ) : data?.orderCount === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 6 }}>
          {t('custHist.empty')}
        </p>
      ) : data ? (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SummaryLine data={data} />
          {(data.products?.length ?? 0) > 0 && <ProductsList data={data} />}
          {(data.channels?.length ?? 0) > 0 && <ChannelsList data={data} />}
          <Counters data={data} />
          {data.completedCount > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('custHist.completedTotal', { amount: formatMoney(data.completedTotal) })}
            </p>
          )}
          {data.sampled && (
            <p
              style={{
                fontSize: 11.5,
                color: 'var(--text-secondary)',
                margin: 0,
                fontStyle: 'italic',
              }}
            >
              {t('custHist.sampledNote')}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SummaryLine({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCustomerOrderSummary>['data']>;
}) {
  const parts: string[] = [];
  if (data.avgIntervalDays !== null) {
    parts.push(
      t('custHist.avgInterval', {
        n: data.avgIntervalDays.toLocaleString('tr-TR', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      }),
    );
  }
  if (data.typicalWeekday !== null) {
    parts.push(
      t('custHist.typicalWeekday', {
        weekday: t(`custHist.weekday.${data.typicalWeekday}` as Parameters<typeof t>[0]),
      }),
    );
  }
  if (data.lastOrderAt) {
    parts.push(t('custHist.lastOrder', { date: formatDate(data.lastOrderAt) }));
  }
  if (parts.length === 0) return null;
  return (
    <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0, color: 'var(--text-primary)' }}>
      {parts.join(' · ')}
    </p>
  );
}

/**
 * Ürün türü etiketi (`custHist.productLine`'ın "{kind}" kısmı).
 *
 * `t()` bilinmeyen anahtarda `defaultValue` DESTEKLEMEZ (bkz. `packages/shared/src/i18n/index.ts`)
 * — doğrudan `t(\`productKind.${kind}\`)` çağrısı `tr[key]` undefined dönerse `replaceAll`
 * `undefined` üzerinde patlar ve kartı çökertir. Anahtar `tr` içinde yoksa ham `kind` gösterilir.
 */
function productKindLabel(kind: string): string {
  const key = `productKind.${kind}`;
  return key in tr ? t(key as MessageKey) : kind;
}

function ProductsList({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCustomerOrderSummary>['data']>;
}) {
  const visible = data.products.slice(0, 5);
  const rest = data.products.length - visible.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {visible.map((p) => (
        <div
          key={p.productId}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: 13.5,
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {t('custHist.productLine', { name: p.name, kind: productKindLabel(p.kind) })}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>
            {t('custHist.productStats', { qty: p.qty, orderCount: p.orderCount })}
          </span>
        </div>
      ))}
      {rest > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
          {t('custHist.moreProducts', { n: rest })}
        </p>
      )}
    </div>
  );
}

function ChannelsList({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCustomerOrderSummary>['data']>;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {data.channels.map((c) => (
        <span key={c.channel} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ChannelPill channel={c.channel} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {t('custHist.channelLine', { n: c.count })}
          </span>
        </span>
      ))}
    </div>
  );
}

function Counters({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCustomerOrderSummary>['data']>;
}) {
  const parts: string[] = [];
  if (data.completedCount > 0)
    parts.push(t('custHist.count.completed', { n: data.completedCount }));
  if (data.cancelledCount > 0)
    parts.push(t('custHist.count.cancelled', { n: data.cancelledCount }));
  if (parts.length === 0) return null;
  return (
    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>{parts.join(' · ')}</p>
  );
}

/**
 * Sipariş geçmişi listesi (müşteri kartında, LedgerEntries'in üstünde).
 *
 * Mevcut `useOrders` uçtan uca kullanır (`customerId` + `pageSize=20` + `sort=receivedAt`
 * + `dir=desc`); "Daha fazla göster" sonraki sayfayı İSTER ama `useOrders` her sayfa için
 * ayrı bir sorgu açtığından (`queryKey` sayfayı içerir) o sayfanın yanıtı ÖNCEKİ sayfaların
 * yerine geçerdi — bu yüzden sayfalar `pagesRef` ile sayfa numarasına göre biriktirilir ve
 * `page`'e kadar olanlar art arda eklenerek gösterilir. Arka plan yenilemesi (`useOrders`'ın
 * kendi 15 sn'si) yalnız GÜNCEL sayfanın dilimini günceller, önceki sayfaları SİLMEZ.
 * Müşteri değişince sayfa ve birikim sıfırlanır. Satır tıklaması bu kapsamda YOK.
 *
 * HANDLED_EXTERNALLY: cari/stok defterine yansımaz; rozet gri ve title ile açıklanır.
 */
function OrderHistory({ customerId }: { customerId: string }) {
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState<Record<number, Order[]>>({});
  // Sayfa 1'i son gördüğümüzdeki `total` — arka planda değişirse (yeni sipariş geldi ya da
  // sayı kaydı) sayfa sınırları kaymış demektir; birikim eskir.
  const baseTotalRef = useRef<number | null>(null);
  const orders = useOrders({
    customerId,
    pageSize: '20',
    sort: 'receivedAt',
    dir: 'desc',
    // dateFilter göndermiyoruz → varsayılan 'all' (tüm zamanlar).
    page: String(page),
  });

  // Müşteri değişince sıfırla; aynı state başka müşterinin sayfalarını göstermesin.
  useEffect(() => {
    setPage(1);
    setPages({});
    baseTotalRef.current = null;
  }, [customerId]);

  // Gelen sayfayı numarasına göre biriktir (arka plan yenilemesi aynı sayfayı GÜNCELLER,
  // önceki sayfaları silmez). `total` en son gördüğümüzden FARKLIYSA (2. sayfadayken yeni
  // sipariş geldi/silindi — sayfa sınırları kaydı) birikim atılır, 1. sayfaya dönülür; aksi
  // halde aynı sipariş iki sayfada görünebilir ya da en yeni sipariş hiç görünmez.
  useEffect(() => {
    if (!orders.data) return;
    const total = orders.data.total;
    if (baseTotalRef.current !== null && total !== baseTotalRef.current && page !== 1) {
      baseTotalRef.current = total;
      setPage(1);
      setPages({});
      return;
    }
    baseTotalRef.current = total;
    setPages((prev) => ({ ...prev, [page]: orders.data.items }));
  }, [orders.data, page]);

  // Biriktirilmiş sayfalarda aynı sipariş birden fazla kez görünebilir (sayfa sınırı kayması,
  // arka plan yenilemesi) — id'ye göre tekilleştir, ilk görülen (üstteki, en yeni) sayfadan gelen kalır.
  const items = useMemo(() => {
    const seen = new Set<string>();
    const acc: Order[] = [];
    for (let p = 1; p <= page; p += 1) {
      for (const o of pages[p] ?? []) {
        if (seen.has(o.id)) continue;
        seen.add(o.id);
        acc.push(o);
      }
    }
    return acc;
  }, [pages, page]);
  const total = orders.data?.total ?? 0;
  const hasMore = items.length < total;
  // isPending/isError yalnız ELİMİZDE HİÇ satır yokken tüm bölümü kaplar; liste doluyken
  // sayfa geçişi/arka plan yenilemesi sırasında bu dallar birikmiş listeyi GİZLEMEZ.
  const showFullLoading = items.length === 0 && orders.isPending;
  const showFullError = items.length === 0 && orders.isError && !orders.isPending;
  const showInlineError = items.length > 0 && orders.isError;

  return (
    <div>
      <div className="section-label" style={{ marginBottom: 8 }}>
        {t('custHist.historyTitle')}
      </div>
      {showFullLoading ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('custHist.loading')}</p>
      ) : showFullError ? (
        <p style={{ fontSize: 13.5, color: 'var(--danger)' }}>{t('custHist.error')}</p>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
          {t('custHist.historyEmpty')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((o) => (
            <OrderHistoryRow key={o.id} order={o} />
          ))}
          {showInlineError && (
            <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
              {t('custHist.error')}
            </p>
          )}
          {hasMore && (
            <button
              type="button"
              className="btn"
              style={{ marginTop: 10, fontSize: 12.5 }}
              onClick={() => setPage((p) => p + 1)}
              disabled={orders.isFetching}
            >
              {t('custHist.historyLoadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function OrderHistoryRow({
  order,
}: {
  order: NonNullable<ReturnType<typeof useOrders>['data']>['items'][number];
}) {
  const external = order.status === 'HANDLED_EXTERNALLY';
  const faded = order.status === 'CANCELLED' || order.status === 'FAILED';
  return (
    <div
      data-testid={`customer-order-row-${order.id}`}
      data-status={order.status}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 4,
        padding: '8px 0',
        borderBottom: '1px solid var(--border)',
        opacity: faded ? 0.6 : 1,
      }}
      title={external ? t('custHist.externalTooltip') : undefined}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{formatDateTime(order.receivedAt)}</div>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {order.items
            .map((it) => t('custHist.itemsSummary', { n: it.qty, name: it.product.name }))
            .join(', ')}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{formatMoney(order.totalAmount)}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ChannelPill channel={order.channel} />
          {external ? (
            <span
              data-testid="customer-order-external-badge"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '1px 6px',
                background: 'var(--page-bg)',
              }}
              title={t('custHist.externalTooltip')}
            >
              {statusText(order.status, order.externalStatusRaw)}
            </span>
          ) : (
            <StatusDot status={order.status} externalStatusRaw={order.externalStatusRaw} />
          )}
        </span>
      </div>
    </div>
  );
}

/** Sunucunun "borcu aşıyor" yanıtı: onay kutusunun rakamları buradan gelir, panelde üretilmez. */
interface AdvanceConfirm {
  amount: string;
  clientId: string;
  debt: string;
  excess: string;
}

/**
 * 400 gövdesinde `debt`/`excess` varsa istenen şey avans onayıdır; başka her hata olağan
 * yolundan (kırmızı satır) gösterilir. Tutarlar sunucunun `Decimal` ile yazdığı string'lerdir.
 */
function advanceFrom(err: unknown, amount: string, clientId: string): AdvanceConfirm | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body as { debt?: unknown; excess?: unknown } | null;
  if (typeof body?.debt !== 'string' || typeof body.excess !== 'string') return null;
  return { amount, clientId, debt: body.debt, excess: body.excess };
}

/**
 * Tahsilat formu (§3.8 D.3).
 *
 * Tutar SUNUCUYA olduğu gibi gider; panel para hesabı yapmaz (kural 4). `clientId` çift tıklamada
 * ikinci satır yazılmasını engeller — patron "kaydedilmedi mi?" diye ikinci kez basabilir.
 *
 * BORCU AŞAN TUTAR (Faz 8 denetimi B-08): istek önce BAYRAKSIZ gider. Aşıyorsa sunucu hiçbir
 * satır yazmadan 400 döner ve güncel borcu + aşan kısmı gövdesinde verir; onay kutusu bu
 * rakamlarla kurulur. Panel ne çıkarma yapar ne de önbellekteki bayat bakiyeye güvenir. Onay
 * gelirse AYNI `clientId` ile tekrar gönderilir: onaya iki kez basmak ikinci satır yazmaz.
 */
function CollectForm({
  customerId,
  balance,
  autoOpen,
}: {
  customerId: string;
  balance?: string;
  autoOpen?: boolean;
}) {
  const { collect } = useFinanceMutations();
  const [open, setOpen] = useState(!!autoOpen);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'CASH' | 'CARD'>('CASH');
  const [invalid, setInvalid] = useState(false);
  /// Doluysa sunucu "bu tutar borcu aşıyor" dedi ve yazım onay bekliyor.
  const [advance, setAdvance] = useState<AdvanceConfirm | null>(null);

  function send(clean: string, clientId: string, allowAdvance: boolean): void {
    collect.mutate(
      { customerId, amount: clean, method, clientId, allowAdvance: allowAdvance || undefined },
      {
        onSuccess: () => {
          setOpen(false);
          setAmount('');
          setAdvance(null);
        },
        onError: (err) => setAdvance(advanceFrom(err, clean, clientId)),
      },
    );
  }

  function save(): void {
    // Rakam dışını silmek ondalık ayracını da siliyordu: "1.250,75" deftere 125.075 ₺ olarak
    // yazılıyordu (Faz 3 denetimi K2). Geçersiz girişte form KAPANMAZ, hata gösterir.
    const clean = parseMoneyInput(amount);
    if (clean === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setAdvance(null);
    // Bayraksız gider: borcu aşıyorsa sunucu 400 döner ve HİÇBİR SATIR YAZILMAZ.
    send(clean, crypto.randomUUID(), false);
  }

  if (!open) {
    return (
      <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setOpen(true)}>
        {t('cash.collect')}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Mevcut bakiye — ekstresle aynı dil (B/A eki); tahsilat tutarı DEĞİŞMEZ. */}
      {balance ? (
        <div
          data-testid="customer-collect-balance"
          style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}
        >
          {t('panel.balance')}: {formatBalance(balance)}
        </div>
      ) : null}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('cash.collectAmount')}
        </span>
        <input
          placeholder={t('cash.collectAmount')}
          inputMode="decimal"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            // Tutar değişti: eski onay kutusu başka bir tutarı anlatıyor, kapanır.
            setAdvance(null);
          }}
          style={{ height: 46, fontVariantNumeric: 'tabular-nums' }}
        />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['CASH', 'CARD'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            aria-pressed={method === m}
            style={{
              flex: 1,
              borderRadius: 'var(--r-row)',
              padding: '10px 0',
              fontWeight: 600,
              fontSize: 13.5,
              background: method === m ? 'var(--primary-bg)' : 'var(--surface)',
              border: `1px solid ${method === m ? 'var(--primary)' : 'var(--border)'}`,
            }}
          >
            {m === 'CASH' ? t('cash.methodCash') : t('cash.methodCard')}
          </button>
        ))}
      </div>
      {invalid ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('cash.amountInvalid')}</p>
      ) : null}
      {collect.isError && advance === null ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>
          {(collect.error as { message?: string } | null)?.message ?? t('error.internal')}
        </p>
      ) : null}
      {advance ? (
        <div
          style={{
            borderRadius: 'var(--r-row)',
            border: '1px solid var(--warning)',
            background: 'var(--bg-warning-soft)',
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <span
            style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, color: 'var(--warning)' }}
          >
            {t('cash.advanceBadge').toLocaleUpperCase('tr-TR')}
          </span>
          <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
            {t('cash.advanceConfirm', {
              amount: formatMoney(advance.amount),
              debt: formatMoney(advance.debt),
              excess: formatMoney(advance.excess),
            })}
          </p>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn"
          style={{ flex: 1 }}
          onClick={() => {
            setOpen(false);
            setAdvance(null);
          }}
        >
          {t('common.cancel')}
        </button>
        {advance ? (
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={() => send(advance.amount, advance.clientId, true)}
            disabled={collect.isPending}
          >
            {t('cash.advanceConfirmAccept')}
          </button>
        ) : (
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={save}
            disabled={collect.isPending}
          >
            {t('common.save')}
          </button>
        )}
      </div>
    </div>
  );
}

/** Marka bazlı zimmet (Faz 3.2): hangi markadan kaç damacana müşteride. */
function DepositBreakdown({ customerId }: { customerId: string }) {
  const deposits = useCustomerDeposits(customerId);
  const rows = deposits.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
      <div className="section-label" style={{ marginBottom: 6 }}>
        {t('cash.depositsByBrand')}
      </div>
      {rows.map((r) => (
        <div
          key={r.productId}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13.5,
            padding: '3px 0',
          }}
        >
          <span>{r.productName}</span>
          <span style={{ fontWeight: 600 }}>{r.qty}</span>
        </div>
      ))}
    </div>
  );
}

/** Cari hareketler DEFTERDEN okunur; depozito satırları bakiyeye girmediği için işaretlenir. */
function LedgerEntries({ customerId }: { customerId: string }) {
  const txns = useCustomerTxns(customerId);
  const rows = txns.data?.items ?? [];
  return (
    <div>
      <div className="section-label" style={{ marginBottom: 6 }}>
        {t('panel.ledgerEntries')}
      </div>
      {/* Tek satır bağlam: Hayat'ta kapanan siparişler deftere yazılmaz; bayi "cariye neden
          yansımadı?" diye şaşırmasın. */}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
        {t('custHist.ledgerNote')}
      </p>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
          {t('panel.noLedgerEntries')}
        </p>
      ) : (
        rows.map((txn) => {
          const amount = Number(txn.amount);
          return (
            <div
              key={txn.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span>
                <span style={{ display: 'block', fontWeight: 500, fontSize: 13.5 }}>
                  {t(`txnKind.${txn.kind}` as Parameters<typeof t>[0])}
                  {!txn.inBalance ? (
                    <span
                      style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 400 }}
                    >
                      {' '}
                      · {t('cash.notInBalance')}
                    </span>
                  ) : null}
                </span>
                {/* Defter mantığına dokunmadan görünüm: tarihin yanında saat. */}
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {formatDateTime(txn.occurredAt)}
                </span>
                {txn.note ? (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {txn.note}
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: amount > 0 ? 'var(--danger)' : 'var(--success)',
                }}
              >
                {amount > 0 ? '−' : '+'}
                {formatMoney(Math.abs(amount))}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
